import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { QueryClientProvider } from "@tanstack/react-query";
import { env, isSupabaseConfigured } from "@/lib/env";
import { buildEmailAuthPath, getEmailAuthModeForAction, type EmailAuthMode } from "@/lib/auth/emailAuth";
import { toRelativeAppPath } from "@/lib/auth/kakaoOidc";
import { startKakaoLogin } from "@/lib/auth/kakaoLoginStart";
import { startKakaoLoginViaExternalBrowser } from "@/lib/auth/kakaoExternalBrowserHandoff";
import {
  detectKakaoLoginRuntime,
  shouldRecommendExternalBrowserForKakao,
} from "@/lib/auth/kakaoRuntime";
import { clearPendingAuthConsent, loadPendingAuthConsent } from "@/lib/auth/pendingAuthConsent";
import { completePendingAuthSetup } from "@/lib/auth/postLoginSetup";
import { recordAuthConsents } from "@/lib/auth/clientActions";
import { updateMarketingPreferences } from "@/lib/marketing/clientActions";
import { trackKakaoLoginFailed } from "@/lib/analytics/kakaoLoginAnalytics";
import { clearSentryUser, setSentryUser } from "@/lib/observability/sentry";
import { queryClient } from "@/lib/queryClient";
import { getPendingAuthAction, setPendingAuthAction, type PendingAuthAction } from "@/lib/auth/pendingAction";
import {
  clearUserLocaleOverride,
  resolveCurrentAppLocale,
  setUserLocaleOverride,
} from "@/lib/i18n/locale";
import { fetchUserProfilePreferences } from "@/lib/profile/preferences";
import { supabase } from "@/lib/supabase/client";
import { getWebWinbackEntry } from "@/lib/winback/webEntry";
import { LocaleProvider } from "./LocaleProvider";
import type { AppLocale } from "@/lib/i18n/locale";

type AppSessionContextValue = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  pendingAction: PendingAuthAction | null;
  signInWithKakao: (action?: PendingAuthAction) => Promise<void>;
  startEmailAuth: (action?: PendingAuthAction, mode?: EmailAuthMode) => Promise<void>;
  signInWithPreferredAuth: (action?: PendingAuthAction) => Promise<void>;
  clearPendingAction: () => void;
  signOut: () => Promise<void>;
};

const AppSessionContext = createContext<AppSessionContextValue | null>(null);

function isInvalidRefreshTokenError(error: unknown) {
  if (!error || typeof error !== "object") {
    return false;
  }

  const message = "message" in error && typeof error.message === "string"
    ? error.message
    : "";

  return /invalid refresh token|refresh token not found/i.test(message);
}

export function AppProviders({ children, initialLocale }: PropsWithChildren<{ initialLocale?: AppLocale }>) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingAction, setPendingActionState] = useState<PendingAuthAction | null>(() => getPendingAuthAction());

  useEffect(() => {
    if (!session?.user?.id) {
      clearUserLocaleOverride();
      return;
    }

    let cancelled = false;
    void fetchUserProfilePreferences(session.user.id)
      .then((preferences) => {
        if (!cancelled) {
          setUserLocaleOverride(session.user.id, preferences.locale);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Keep the hostname/device locale when the optional profile read is unavailable.
          clearUserLocaleOverride(session.user.id);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  useEffect(() => {
    let mounted = true;

    async function loadInitialSession() {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error && isInvalidRefreshTokenError(error)) {
          await supabase.auth.signOut({ scope: "local" });
        }

        if (mounted) {
          setSession(error ? null : data.session);
          setIsLoading(false);
        }
      } catch {
        if (mounted) {
          setSession(null);
          setIsLoading(false);
        }
      }
    }

    void loadInitialSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      // Synchronous local cleanup only; never await another Supabase request
      // inside the Auth callback or carry an old user's SMS attribution forward.
      if (_event === "SIGNED_OUT") getWebWinbackEntry().clear();
      else if (nextSession?.user.id) getWebWinbackEntry().syncIdentity(nextSession.user.id);
      setSession(nextSession);
      setIsLoading(false);
      setPendingActionState(getPendingAuthAction());
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (session?.user?.id) {
      setSentryUser(session.user.id);
      return;
    }

    clearSentryUser();
  }, [session?.user?.id]);

  useEffect(() => {
    if (!session?.access_token) {
      return;
    }

    // CAUTION: protected Kakao/signup flow. Retry pending consent in the background,
    // but never block an already-authenticated customer from entering the app.
    const pendingConsent = loadPendingAuthConsent();
    if (!pendingConsent) {
      return;
    }

    void completePendingAuthSetup({
      pendingConsent,
      recordAuthConsents,
      updateMarketingPreferences,
      recordRequiredConsents: pendingAction?.type !== "bootstrap-household",
    }).then(() => {
      clearPendingAuthConsent();
    }).catch(() => {
      // Do not block app entry when consent sync fails.
    });
  }, [pendingAction?.type, session?.access_token]);

  const value = useMemo<AppSessionContextValue>(
    () => {
      const rememberAction = (action?: PendingAuthAction) => {
        if (!action) {
          return;
        }

        setPendingAuthAction(action);
        setPendingActionState(action);
      };

      const signInWithKakao = async (action?: PendingAuthAction) => {
        if (!isSupabaseConfigured) {
          throw new Error("Supabase environment is not configured.");
        }

        rememberAction(action);

        const next = action?.redirectTo
          ? toRelativeAppPath(action.redirectTo)
          : "/app";

        const runtime = detectKakaoLoginRuntime();
        if (shouldRecommendExternalBrowserForKakao(runtime)) {
          await startKakaoLoginViaExternalBrowser({
            next,
            continueInApp: () => startKakaoLogin({ next }),
            onBlocked: () => trackKakaoLoginFailed({
              step: "external_browser_handoff",
              errorCode: "external_browser_blocked",
              userAgent: runtime.userAgent,
            }),
          });
          return;
        }

        await startKakaoLogin({ next });
      };

      const startEmailAuth = async (action?: PendingAuthAction, mode?: EmailAuthMode) => {
        if (!isSupabaseConfigured) {
          throw new Error("Supabase environment is not configured.");
        }

        rememberAction(action);

        const next = action?.redirectTo
          ? toRelativeAppPath(action.redirectTo)
          : "/app";

        window.location.assign(buildEmailAuthPath(next, mode ?? getEmailAuthModeForAction(action)));
      };

      return {
        session,
        user: session?.user ?? null,
        isLoading,
        pendingAction,
        signInWithKakao,
        startEmailAuth,
        signInWithPreferredAuth(action) {
          return resolveCurrentAppLocale() === "en"
            ? startEmailAuth(action)
            : signInWithKakao(action);
        },
        clearPendingAction() {
          setPendingAuthAction(null);
          setPendingActionState(null);
        },
        async signOut() {
          await supabase.auth.signOut();
          clearUserLocaleOverride(session?.user?.id);
          setPendingAuthAction(null);
          setPendingActionState(null);
        },
      };
    },
    [isLoading, pendingAction, session],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AppSessionContext.Provider value={value}>
        <LocaleProvider initialLocale={initialLocale}>{children}</LocaleProvider>
      </AppSessionContext.Provider>
    </QueryClientProvider>
  );
}

export function useAppSession() {
  const context = useContext(AppSessionContext);
  if (!context) {
    throw new Error("useAppSession must be used inside AppProviders");
  }
  return context;
}
